import React, { useCallback, useEffect, useRef } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';

import {
  AccountManager,
  btag,
  constants,
  pushToDataLayer,
  sessionId,
  SessionStorageManager,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useDetection,
} from '@atlas/core';
import { onSignUpStepSubmit, onSignUpSubmit, useActiveForm } from '@atlas/react-forms';
import { clientProfileRoutes, CmdLink, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';
import { HeaderTitle, Label } from '@atlas/ui';

import {
  ACCOUNT_STATUSES,
  BTAG_STORAGE_KEY,
  CXFROM_STORAGE_KEY,
  DATA_LAYER_STORAGE_KEYS,
  OAUTH_FLOW_TYPES,
} from 'common/constants';
import StaticBlock from 'common/StaticBlock/StaticBlock';
import Footer from 'components/Footer/Footer';
import { useHidePopupSignUpDelay } from 'hooks/useHidePopupSignUpDelay';
import { usePostHog, useRegistrationPostHog } from 'hooks/usePostHog';
import { useSetNextShowPopupSignUpDelay } from 'hooks/useSetNextShowPopupSignUpDelay';
import useSocialProviders from 'src/components/SignUp/useSocialProviders';

import useAutoAdvancePrefilledSteps from './hooks/useAutoAdvancePrefilledSteps';
import useOAuthRegistrationForm from './hooks/useOAuthRegistrationForm';
import useTouchPrefilledFields from './hooks/useTouchPrefilledFields';
import { getMultistepConfig } from './getMultistepConfig';
import Navigation from './Navigation';
import SignUpFormBody from './SignUpFormBody';
import useLeaveSignupBlocker from './useLeaveSignupBlocker';
import useRegistrationFlowActive from './useRegistrationFlowActive';
import useRegistrationStepAnalytics from './useRegistrationStepAnalytics';
import useSetMultiStepForPartiallyRegistered from './useSetMultiStepForPartiallyRegistered';
import useSignUpAction from './useSignUpAction';
import useStepFields from './useStepFields';

const { payment } = clientProfileRoutes;
const { deposit } = paymentRoutes;
const { CLIENT_PROFILE } = COMPONENT_TYPES;

export const GTM_TRACKED_FIELDS = ['PHONE', 'EMAIL', 'DOCUMENT_NUMBER'];
export const gtmFieldChangesHandler = fieldName => {
  switch (fieldName) {
    case 'PHONE':
      return pushToDataLayer('end_phone_input', btag);
    case 'EMAIL':
      return pushToDataLayer('end_email_input', btag);
    case 'DOCUMENT_NUMBER':
      return pushToDataLayer('end_document_input', btag);
  }
};

const SignUp = () => {
  const formRef = useRef(null);
  const bypassLeaveBlockRef = useRef(false);
  const postHog = usePostHog();
  const { search } = useLocation();
  const { account, authenticated, resolved } = useApplicationState();
  const { browser, ip, locale, os } = useDetection();

  const cxFromPath = StorageManager.getItem(CXFROM_STORAGE_KEY);
  const params = new URLSearchParams(search);
  const cxFromParam = params.get('cxfrom');
  const history = useHistory();
  const leaveSignup = SiteConfigManager.getConfig(
    'config.config.general.leaveRegistrationPopup.enable',
    false,
  );
  const isShownProgressBar = SiteConfigManager.getConfig(
    'config.config.registration.showRegistrationProgressBar',
    true,
  );
  const leaveAfterFirstStep = SiteConfigManager.getConfig(
    'config.config.registration.close_multistep_after_first_step',
    false,
  );

  const { getActiveFormsWithOAuth, isOAuthRegistrationRef } = useOAuthRegistrationForm();

  const {
    activeStep,
    activeType,
    resetActiveForm,
    setActiveStep,
    setActiveType,
    stepSubmit,
    stepsAmount,
    submit,
    types,
  } = useActiveForm(
    'CLIENT_REGISTRATION',
    onSignUpSubmit(formData => {
      pushToDataLayer('registration_form_button_click', btag);
      for (const field in formData) {
        if (formData[field] === null) {
          // eslint-disable-next-line no-param-reassign
          delete formData[field];
        }
      }
      // email hashing will be implement in FT-7781
      return AccountManager.formRegistration(formData).then(result => {
        handleRegistrationSuccess({ clientId: result.clientId, email: formData.EMAIL });
        return result;
      });
    }),
    getActiveFormsWithOAuth,
    onSignUpStepSubmit(formData => {
      return AccountManager.formRegistrationSubmitStep(formData);
    }),
  );

  const isMultiStepFlow = activeType?.templateName === 'MULTI_STEP';

  useSetMultiStepForPartiallyRegistered({
    accountStatus: account.status,
    activeType,
    setActiveType,
    types,
  });

  const isBackButtonVisible = activeStep > (isMultiStepFlow ? 1 : 0);
  const isUserFullyRegistered =
    resolved && authenticated && account.status !== ACCOUNT_STATUSES.PARTIALLY_REGISTERED;

  const { handleBackClick, handleLeave, handleNextStep, handleRegistrationSuccess } =
    useRegistrationPostHog({
      activeStep,
      cxFromParam,
      cxFromPath,
      postHog,
      stepsAmount,
    });

  const onBackHandler = useCallback(() => {
    handleBackClick();
    setActiveStep(activeStep - 1);
  }, [setActiveStep, activeStep, handleBackClick]);

  useHidePopupSignUpDelay();
  useSetNextShowPopupSignUpDelay();
  useRegistrationFlowActive({ enabled: !isUserFullyRegistered && isMultiStepFlow });

  useEffect(() => {
    if (!search) {
      pushToDataLayer('registration_start', { action: 'start' }, btag);
    }
  }, [search]);

  const trackLeavePopupShown = useCallback(() => {
    if (!SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_SHOWN)) {
      pushToDataLayer(
        'exit_popup_shown',
        {
          locale,
          page_referer: window.location.pathname,
          session_id: sessionId,
        },
        btag,
      );

      SessionStorageManager.setItem(DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_SHOWN, true);
    }
  }, [locale]);

  useLeaveSignupBlocker({
    enabled: leaveSignup && !isUserFullyRegistered,
    history,
    isMultiStepFlow,
    isPartiallyRegistered: account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED,
    onLeave: handleLeave,
    onPopupShown: trackLeavePopupShown,
    popupType: 'signupPage',
    shouldBypassBlock: () => bypassLeaveBlockRef.current,
  });

  const { push: historyPush } = useHistory();
  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);

  const defaultPageAfterRegistration = SiteConfigManager.getConfig(
    'config.config.links.defaultPageAfterRegistration',
  );

  const isLastStep = stepsAmount && activeStep === activeType.steps.length - 1;
  const isFirstStep = activeStep === 0;
  const { headerTitle, showSignInLink } = getMultistepConfig(isMultiStepFlow, isFirstStep, account);

  useRegistrationStepAnalytics({
    activeStep,
    browser,
    device: constants?.DEVICE_TYPES?.DESKTOP,
    ip,
    locale,
    os,
    stepsAmount,
  });

  useTouchPrefilledFields({
    activeStep,
    activeType,
    formRef,
    isOAuthRegistration: isOAuthRegistrationRef.current,
  });

  useAutoAdvancePrefilledSteps({
    activeType,
    isOAuthRegistration: isOAuthRegistrationRef.current,
    setActiveStep,
  });

  const redirectAfterRegistration = useCallback(() => {
    const pageAfterRegistration =
      defaultPageAfterRegistration?.indexOf('//') > -1
        ? defaultPageAfterRegistration.split('/').reduce((acc, next, index) => {
            if (index < 3) return acc;
            return `${acc}/${next}`;
          }, '')
        : defaultPageAfterRegistration;

    const isMultiStepFirstStepExit = isMultiStepFlow && isFirstStep && leaveAfterFirstStep;

    const redirectPath = isMultiStepFirstStepExit
      ? '/'
      : cxFromParam ||
        cxFromPath ||
        pageAfterRegistration ||
        `${clientProfilePath}/${payment}/${deposit}`;

    if (
      (SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_CLOSED) ||
        SessionStorageManager.getItem(
          DATA_LAYER_STORAGE_KEYS.LEAVING_REGISTRATION_POPUP_CTA_CLICKED,
        )) &&
      sessionId
    ) {
      pushToDataLayer(
        'exit_popup_converted',
        {
          session_id: sessionId,
        },
        btag,
      );
    }

    // allow post-submit internal redirect without triggering leave popup
    bypassLeaveBlockRef.current = true;

    historyPush(redirectPath);
    StorageManager.removeItem(CXFROM_STORAGE_KEY);
    StorageManager.removeItem(BTAG_STORAGE_KEY);
  }, [
    defaultPageAfterRegistration,
    cxFromPath,
    clientProfilePath,
    historyPush,
    isMultiStepFlow,
    leaveAfterFirstStep,
    isFirstStep,
    cxFromParam,
  ]);

  const action = useSignUpAction({
    activeStep,
    isFirstStep,
    isLastStep,
    isMultiStepFlow,
    leaveAfterFirstStep,
    onNextStep: handleNextStep,
    redirectAfterRegistration,
    resetActiveForm,
    setActiveStep,
    stepSubmit,
    submit,
  });

  const fields = useStepFields({ form: activeType, stepNumber: activeStep });

  const socialProviders = useSocialProviders(activeType);
  const flowType = isMultiStepFlow ? OAUTH_FLOW_TYPES.SIGNUP_MULTISTEP : OAUTH_FLOW_TYPES.SIGNUP;

  const isFormCompletedOrFullyRegistered =
    activeType?.completed === true || account.status !== ACCOUNT_STATUSES.PARTIALLY_REGISTERED;

  const shouldRedirect = resolved && authenticated && isFormCompletedOrFullyRegistered;

  if (shouldRedirect) {
    // prevent leave popup during internal auth/completion redirect
    bypassLeaveBlockRef.current = true;
    return <Redirect to={cxFromParam || '/'} />;
  }

  const shouldShowStepper = !isMultiStepFlow || isShownProgressBar;

  return (
    <div className="container-fluid">
      <div className="signup">
        <div className="signup__container">
          <div className="signup__col">
            <div className="signup__wrapper">
              <HeaderTitle className="center block-lg border" title={headerTitle} />

              {activeType && (
                <>
                  <Navigation activeType={activeType} onChange={setActiveType} types={types} />
                  <SignUpFormBody
                    action={action}
                    activeStep={activeStep}
                    activeType={activeType}
                    fields={fields}
                    flowType={flowType}
                    formRef={formRef}
                    hideSocialProviders={isOAuthRegistrationRef.current}
                    isBackButtonVisible={isBackButtonVisible}
                    isLastStep={isLastStep}
                    onBackHandler={onBackHandler}
                    onTrackedFieldChanges={gtmFieldChangesHandler}
                    shouldShowStepper={shouldShowStepper}
                    showSignInLink={showSignInLink}
                    signInLink={
                      <CmdLink cmd="signin">
                        <Label className="title-bold-text link" message="Sign in" />
                      </CmdLink>
                    }
                    socialProviders={socialProviders}
                    stepsAmount={stepsAmount}
                    trackedFields={GTM_TRACKED_FIELDS}
                  />
                </>
              )}
            </div>
          </div>
          <div className="signup__col__banner">
            <StaticBlock options={{ source: 'registration-banner' }} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SignUp;
